import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    FaIdCard,
    FaCalendarAlt,
    FaCreditCard,
    FaGift,
    FaPercentage,
    FaBolt,
    FaReceipt,
    FaBell,
    FaUser,
    FaEdit,
    FaFolder,
    FaFilePdf,
    FaDownload,
    FaArrowRight,
    FaUserFriends,
    FaExclamationCircle,
    FaChevronDown,
    FaChevronUp,
    FaUserCheck,
    FaClock,
    FaTicketAlt,
    FaSyncAlt,
    FaLock,
    FaTimes,
    FaCheck,
    FaExternalLinkAlt
} from 'react-icons/fa';
import EditProfileModal from '../../components/EditProfileModal/EditProfileModal';
import BecomeMember from '../../components/BecomeMember/BecomeMember';
import FamilyAssociationModal from '../../components/FamilyAssociationModal/FamilyAssociationModal';
import DocumentsModal from '../../components/DocumentsModal/DocumentsModal';
import MembershipCard, { getCardPaymentState } from '../../components/MembershipCard/MembershipCard';
import ReceiptsModal from '../../components/ReceiptsModal/ReceiptsModal';
import TicketsModal from '../../components/TicketsModal/TicketsModal';
import AdminPinModal from '../../components/AdminPinModal/AdminPinModal';
import ConsentRequestModal from '../../components/ConsentRequestModal/ConsentRequestModal';
import FamilyPrivacyModal from '../../components/FamilyPrivacyModal/FamilyPrivacyModal';
import { FaShieldAlt } from 'react-icons/fa';
import './DashboardSocio.css';
import '../../components/Payment/PaymentCard.css';

import PaymentHistorySocio from '../../components/PaymentHistorySocio/PaymentHistorySocio';
import API_BASE_URL from '../../config/api';

// Subcomponente para fazer polling após pagamento com sucesso
const ActivationPolling = ({ userId, onActivated }) => {
    useEffect(() => {
        let isMounted = true;

        const checkStatus = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.memberProfile?.membershipStatus === 1) { // 1 = Active
                        if (isMounted) onActivated();
                    }
                }
            } catch (err) {
                console.error("Polling error:", err);
            }
        };

        const interval = setInterval(checkStatus, 3000); // Check every 3 seconds
        checkStatus(); // Initial check

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [userId, onActivated]);

    return (
        <div className="dashboard-wrapper" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', textAlign: 'center' }}>
            <div style={{
                width: '50px', height: '50px', borderRadius: '50%',
                border: '4px solid rgba(0, 0, 0, 0.1)',
                borderLeftColor: 'var(--primary-color)',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
            }}></div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <h2>A confirmar pagamento...</h2>
            <p style={{ color: 'var(--text-light)', maxWidth: '400px', marginTop: '10px' }}>
                O seu pagamento foi recebido com sucesso. Estamos a finalizar a ativação da sua conta de sócio. Este processo pode demorar alguns segundos.
            </p>
        </div>
    );
};

const DashboardSocio = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [paymentData, setPaymentData] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [memberQuota, setMemberQuota] = useState(null);
    const [quotaBreakdown, setQuotaBreakdown] = useState([]);
    const [appliedDiscounts, setAppliedDiscounts] = useState([]);
    const [overdueMonths, setOverdueMonths] = useState([]);
    const [totalDue, setTotalDue] = useState(null);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
    const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [isOverdueDropdownOpen, setIsOverdueDropdownOpen] = useState(false);
    const [isReceiptsModalOpen, setIsReceiptsModalOpen] = useState(false);
    const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
    const [startingPayment, setStartingPayment] = useState(false);
    const [paymentToast, setPaymentToast] = useState(null); // 'success' | 'cancelled' | null
    const [awaitingActivation, setAwaitingActivation] = useState(false); // Stripe redirect succeeded but webhook pending
    const [refreshKey, setRefreshKey] = useState(0);
    const [showAdminPinModal, setShowAdminPinModal] = useState(false);
    const [pendingSwitchUser, setPendingSwitchUser] = useState(null);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [targetConsentUser, setTargetConsentUser] = useState(null);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedMonthKeys, setSelectedMonthKeys] = useState(new Set());
    const [futureMonths, setFutureMonths] = useState([]);

    const [linkedUsers, setLinkedUsers] = useState(() => {
        try {
            const stored = localStorage.getItem('linkedUsers');
            return stored ? JSON.parse(stored) : [];
        } catch { return []; }
    });
    const [currentUserId, setCurrentUserId] = useState(() => parseInt(localStorage.getItem('userId')) || null);
    const [isSwitchingUser, setIsSwitchingUser] = useState(false);
    const [switchingToName, setSwitchingToName] = useState('');

    const [isPollingPayment, setIsPollingPayment] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paymentParam = params.get('payment');
        const redirectStatus = params.get('redirect_status');

        if (paymentParam === 'success') {
            if (redirectStatus === 'failed') {
                setPaymentToast('cancelled');
                setIsPollingPayment(false);
                setAwaitingActivation(false);
                window.history.replaceState({}, '', location.pathname);
                const timer = setTimeout(() => setPaymentToast(null), 6000);
                return () => clearTimeout(timer);
            } else {
                setIsPollingPayment(true);
                setAwaitingActivation(true);
                window.history.replaceState({}, '', location.pathname);
            }
        } else if (paymentParam === 'cancelled') {
            setPaymentToast('cancelled');
            window.history.replaceState({}, '', location.pathname);
            const timer = setTimeout(() => setPaymentToast(null), 6000);
            return () => clearTimeout(timer);
        }
    }, [location.search]);

    // Background polling for payment status completion
    useEffect(() => {
        if (!isPollingPayment || !currentUserId) return;

        let attempt = 0;
        const maxAttempts = 10;

        const interval = setInterval(async () => {
            attempt++;
            if (attempt >= maxAttempts) {
                setIsPollingPayment(false);
                clearInterval(interval);
                return;
            }

            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/api/payment/quota?userId=${currentUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const quotaData = await response.json();
                    if (quotaData.status === 'Regularizada' || (quotaData.totalDue ?? 0) === 0) {
                        setRefreshKey(prev => prev + 1);
                        setIsPollingPayment(false);
                        clearInterval(interval);
                    }
                }
            } catch (err) {
                console.error("Payment status polling error:", err);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [isPollingPayment, currentUserId]);

    const refreshLinkedUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            if (!token) return;
            const res = await fetch(`${API_BASE_URL}/api/auth/linked-users?primaryUserId=${primaryUserId || ''}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                }
            });
            if (res.ok) {
                const data = await res.json();
                setLinkedUsers(data);
                if (data.length > 0) {
                    localStorage.setItem('linkedUsers', JSON.stringify(data));
                } else {
                    localStorage.removeItem('linkedUsers');
                }
            }
        } catch (err) {
            console.warn('Could not refresh linked users:', err);
        }
    };

    useEffect(() => {
        refreshLinkedUsers();
    }, []);

    const handleLinkedTabClick = async (lu) => {
        if (currentUserId === lu.id) return;

        if (lu.requiresConsent) {
            setTargetConsentUser(lu);
            setShowConsentModal(true);
            return;
        }

        const type = lu.dashboardType?.toLowerCase() || 'socio';
        if (type === 'admin' || lu.requiresPin) {
            setPendingSwitchUser(lu);
            setShowAdminPinModal(true);
            return;
        }

        executeUserSwitch(lu);
    };

    const executeUserSwitch = async (lu) => {
        setIsSwitchingUser(true);
        setSwitchingToName(`${lu.firstName} ${lu.lastName}`);
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            const res = await fetch(`${API_BASE_URL}/api/auth/switch-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                },
                body: JSON.stringify({ targetUserId: lu.id, primaryUserId: primaryUserId ? parseInt(primaryUserId) : null })
            });

            if (res.ok) {
                const data = await res.json();
                localStorage.setItem('token', data.token);
                localStorage.setItem('userId', data.id.toString());
                localStorage.setItem('roles', JSON.stringify(data.roles));
                localStorage.setItem('userName', `${data.firstName} ${data.lastName}`);
                localStorage.setItem('userEmail', data.email);

                const dashboardRoutes = {
                    atleta: '/dashboard-atleta',
                    treinador: '/dashboard-treinador',
                    coordenador: '/dashboard-coordenador',
                    socio: '/dashboard-socio',
                    admin: '/dashboard-admin',
                    user: '/dashboard-socio'
                };
                const type = lu.dashboardType?.toLowerCase() || 'socio';
                const route = dashboardRoutes[type] || '/dashboard-socio';

                if (route !== '/dashboard-socio') {
                    navigate(route);
                    window.location.reload();
                } else {
                    setCurrentUserId(data.id);
                    window.location.reload();
                }
            } else {
                const err = await res.json().catch(() => ({}));
                alert(err.message || 'Erro ao alterar perfil');
                setIsSwitchingUser(false);
            }
        } catch (err) {
            console.error('Error switching user:', err);
            setIsSwitchingUser(false);
        }
    };

    const handleStartPayment = (selectedOverdueMonths = null, includesCurrentMonth = true, selectedAmount = null, selectedFutureMonths = [], currentMonthItem = null) => {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/login'); return; }

        const isAnnual = paymentData?.paymentPreference === 'Annual';

        // Effective overdue: what was selected in the modal (or all if called without modal)
        // Future months are merged in — they're just additional periods from the backend's perspective
        const effectiveOverdue = [
            ...(selectedOverdueMonths !== null ? selectedOverdueMonths : overdueMonths),
            ...(selectedFutureMonths || [])
        ];
        const effectiveAmount = selectedAmount !== null
            ? selectedAmount
            : (overdueMonths.length > 0 ? totalDue : memberQuota);

        // Current period
        const pMonth = includesCurrentMonth
            ? (isAnnual ? null : (new Date().getMonth() + 1))
            : null;
        const pYear = includesCurrentMonth
            ? new Date().getFullYear()
            : null;

        let description = '';
        const totalMonths = effectiveOverdue.length + (includesCurrentMonth ? 1 : 0);
        if (effectiveOverdue.length > 0 && !includesCurrentMonth) {
            description = `Quotas — ${effectiveOverdue.length} ${effectiveOverdue.length === 1 ? 'mês' : 'meses'}`;
        } else if (effectiveOverdue.length > 0) {
            description = `Quotas — ${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'}`;
        } else if (pMonth && pYear) {
            if (isAnnual) {
                const startMonthName = new Date(pYear, pMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                const endMonth = pMonth === 1 ? 12 : pMonth - 1;
                const endYear = pMonth === 1 ? pYear : pYear + 1;
                const endMonthName = new Date(endYear, endMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                description = `Quota Anual — ${startMonthName.charAt(0).toUpperCase() + startMonthName.slice(1)} de ${pYear} a ${endMonthName.charAt(0).toUpperCase() + endMonthName.slice(1)} de ${endYear}`;
            } else {
                const monthName = new Date(pYear, pMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
                description = `Quota Mensal — ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
            }
        }

        navigate('/checkout', {
            state: {
                type: 'quota',
                amount: effectiveAmount,
                description,
                selectedUserId: currentUserId,
                buyerEmail: userData?.email || '',
                periodYear: pYear,
                periodMonth: pMonth,
                overdueMonths: effectiveOverdue,
                breakdown: currentMonthItem?.breakdown || quotaBreakdown,
                currentMonthDetails: currentMonthItem,
                isAnnual,
                successPath: '/dashboard-socio',
                cancelPath: '/dashboard-socio',
                queryParams: '?payment=success',
            }
        });
    };

    // ── Payment modal helpers ──────────────────────────────────────────────────
    const isAnnualPref = paymentData?.paymentPreference === 'Annual';

    const allPayableMonths = useMemo(() => {
        const now = new Date();
        const realCurrentMonth = now.getMonth() + 1;
        const realCurrentYear = now.getFullYear();

        const historyList = Array.isArray(paymentHistory) ? paymentHistory : (paymentHistory?.payments || []);

        const months = (overdueMonths || []).map(m => ({
            key: `${m.periodMonth ?? 'annual'}-${m.periodYear}`,
            periodMonth: m.periodMonth,
            periodYear: m.periodYear,
            amount: m.amount || 0,
            isCurrent: false,
            isFuture: false,
        }));

        const isCurrentMonthPaid = paymentData?.status === 'Regularizada' || paymentData?.paymentStatus === 'Regularizada' || historyList.some(
            p => p.periodYear === realCurrentYear && p.periodMonth === realCurrentMonth && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
        );

        // Current month (only add if monthly and not already paid)
        if (!isAnnualPref && !isCurrentMonthPaid) {
            const alreadyIn = months.some(m => m.periodMonth === realCurrentMonth && m.periodYear === realCurrentYear);
            if (!alreadyIn) {
                const overdueTotal = (overdueMonths || []).reduce((s, m) => s + (m.amount || 0), 0);
                const currentAmt = totalDue != null && totalDue > 0
                    ? Math.max(totalDue - overdueTotal, memberQuota || 0)
                    : (memberQuota || 0);
                months.push({
                    key: `current-${realCurrentMonth}-${realCurrentYear}`,
                    periodMonth: realCurrentMonth,
                    periodYear: realCurrentYear,
                    amount: currentAmt,
                    isCurrent: true,
                    isFuture: false,
                });
            }
        }

        // Future months
        if (!isAnnualPref) {
            if (futureMonths && futureMonths.length > 0) {
                futureMonths.forEach(fm => {
                    const isPaidInHistory = historyList.some(
                        p => p.periodMonth === fm.periodMonth && p.periodYear === fm.periodYear && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
                    );
                    const alreadyIn = months.some(m => m.periodMonth === fm.periodMonth && m.periodYear === fm.periodYear);
                    if (!alreadyIn && !isPaidInHistory) {
                        months.push({
                            key: `future-${fm.periodMonth}-${fm.periodYear}`,
                            periodMonth: fm.periodMonth,
                            periodYear: fm.periodYear,
                            amount: fm.amount || 0,
                            isCurrent: false,
                            isFuture: true,
                        });
                    }
                });
            } else if ((memberQuota || 0) > 0) {
                for (let i = 1; i <= 11; i++) {
                    const d = new Date();
                    d.setMonth(d.getMonth() + i);
                    const fMonth = d.getMonth() + 1;
                    const fYear = d.getFullYear();
                    const isPaidInHistory = historyList.some(
                        p => p.periodMonth === fMonth && p.periodYear === fYear && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
                    );
                    const alreadyIn = months.some(m => m.periodMonth === fMonth && m.periodYear === fYear);
                    if (!alreadyIn && !isPaidInHistory) {
                        months.push({
                            key: `future-${fMonth}-${fYear}`,
                            periodMonth: fMonth,
                            periodYear: fYear,
                            amount: memberQuota || 0,
                            isCurrent: false,
                            isFuture: true,
                        });
                    }
                }
            }
        }
        return months;
    }, [overdueMonths, futureMonths, memberQuota, totalDue, isAnnualPref, paymentData, paymentHistory]);

    const openPaymentModal = () => {
        const preSelected = allPayableMonths.filter(m => !m.isFuture).map(m => m.key);
        setSelectedMonthKeys(new Set(preSelected));
        setShowPaymentModal(true);
    };

    const toggleMonthSel = (key) => {
        setSelectedMonthKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectedTotal = allPayableMonths
        .filter(m => selectedMonthKeys.has(m.key))
        .reduce((sum, m) => sum + m.amount, 0);

    const handleConfirmModalPayment = () => {
        const selectedOverdue = (overdueMonths || []).filter(m =>
            selectedMonthKeys.has(`${m.periodMonth ?? 'annual'}-${m.periodYear}`)
        );
        const currentItem = allPayableMonths.find(m => m.isCurrent);
        const includesCurrent = currentItem ? selectedMonthKeys.has(currentItem.key) : false;
        const selectedFuture = allPayableMonths
            .filter(m => m.isFuture && selectedMonthKeys.has(m.key))
            .map(({ periodMonth, periodYear, amount }) => ({ periodMonth, periodYear, amount }));
        setShowPaymentModal(false);
        handleStartPayment(selectedOverdue, includesCurrent, selectedTotal, selectedFuture);
    };
    // ── End payment modal helpers ──────────────────────────────────────────────


    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    navigate('/login');
                    return;
                }

                setLoading(true);
                const userResponse = await fetch(`${API_BASE_URL}/api/users/${currentUserId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!userResponse.ok) {
                    if (userResponse.status === 401) {
                        localStorage.removeItem('token');
                        navigate('/login');
                        return;
                    }
                    throw new Error('Failed to fetch user data');
                }

                const data = await userResponse.json();
                const statusMap = { 0: 'Pending', 1: 'Active', 2: 'Suspended', 3: 'Cancelled' };
                const uData = {
                    ...data,
                    membershipStatus: statusMap[data.memberProfile?.membershipStatus] ?? 'Pending',
                    memberSince: data.memberProfile?.memberSince ?? null,
                };
                setUserData(uData);

                if (uData.membershipStatus === 'Active') {
                    const quotaResponse = await fetch(`${API_BASE_URL}/api/payment/quota?userId=${currentUserId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (quotaResponse.ok) {
                        const qData = await quotaResponse.json();
                        setMemberQuota(qData.amount);
                        setQuotaBreakdown(qData.breakdown || []);
                        setAppliedDiscounts(qData.discountsApplied || []);
                        setOverdueMonths(qData.overdueMonths || []);
                        setFutureMonths(qData.futureMonths || []);
                        setTotalDue(qData.totalDue ?? null);
                        setPaymentData(qData);
                    }

                    const historyResponse = await fetch(`${API_BASE_URL}/api/payment/history`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (historyResponse.ok) {
                        const hData = await historyResponse.json();
                        setPaymentHistory(Array.isArray(hData) ? hData : (hData.payments || []));
                    }
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, currentUserId, refreshKey]);

    const handleEditProfile = () => setIsEditModalOpen(true);
    const handleCloseModal = () => setIsEditModalOpen(false);
    const handleSaveProfile = (updatedData) => setUserData(prev => ({ ...prev, ...updatedData }));

    if (loading) {
        const storedName = localStorage.getItem('userName');
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 15, 38, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 99999,
                animation: 'fadeInOverlay 0.3s ease-out forwards'
            }}>
                <style>{`
                    @keyframes fadeInOverlay {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes pulseLogo {
                        0% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                        50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 25px rgba(255,255,255,0.3)); }
                        100% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                    }
                    @keyframes rotateSpinner {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '24px',
                    textAlign: 'center',
                    padding: '32px',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
                    maxWidth: '400px',
                    width: '90%'
                }}>
                    <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <img
                            src="/CDP_logo.png"
                            alt="CDP Logo"
                            style={{
                                width: '64px',
                                height: '64px',
                                objectFit: 'contain',
                                animation: 'pulseLogo 2s ease-in-out infinite',
                                zIndex: 2
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            width: '84px',
                            height: '84px',
                            borderRadius: '50%',
                            border: '3px solid transparent',
                            borderTopColor: '#003380',
                            borderRightColor: '#0044ff',
                            animation: 'rotateSpinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                            zIndex: 1
                        }}></div>
                    </div>
                    <div>
                        <h3 style={{
                            color: 'white',
                            margin: '0 0 8px 0',
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            letterSpacing: '-0.025em'
                        }}>
                            A carregar...
                        </h3>
                        {storedName && (
                            <p style={{
                                color: '#94a3b8',
                                margin: 0,
                                fontSize: '0.95rem',
                                fontWeight: '500'
                            }}>
                                A entrar como <span style={{ color: '#60a5fa', fontWeight: '600' }}>{storedName}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (error || !userData) {
        return (
            <div className="dashboard-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <h2>Erro ao carregar dados do utilizador</h2>
            </div>
        );
    }

    // ── Polling screen: user paid but webhook not yet processed ────────────────
    if (userData.membershipStatus === 'Pending' && awaitingActivation) {
        return (
            <ActivationPolling
                userId={currentUserId}
                onActivated={() => {
                    setAwaitingActivation(false);
                    setIsPollingPayment(false);
                    setLoading(true);
                    setUserData(null);
                    setRefreshKey(prev => prev + 1);
                }}
            />
        );
    }

    if (userData.membershipStatus === 'Pending') {
        return (
            <div className="dashboard-wrapper">
                {linkedUsers.length > 1 && (
                    <div className="athlete-tabs">
                        <div className="container athlete-tabs-container">
                            {linkedUsers.map((lu) => (
                                <button
                                    key={lu.id}
                                    onClick={() => handleLinkedTabClick(lu)}
                                    className={`athlete-tab ${currentUserId === lu.id ? 'active' : ''} ${lu.requiresConsent ? 'is-blocked' : ''}`}
                                    title={lu.requiresConsent ? 'Requer Autorização (18+ anos)' : lu.requiresPin ? 'Requer PIN (18+ anos)' : ''}
                                >
                                    {lu.requiresConsent ? (
                                        <FaLock style={{ color: '#ef4444', marginRight: '6px' }} />
                                    ) : lu.requiresPin ? (
                                        <FaLock style={{ color: '#eab308', marginRight: '6px' }} />
                                    ) : null}
                                    {`${lu.firstName} ${lu.lastName}`.trim() || `Conta ${lu.id}`}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <BecomeMember userData={userData} />
            </div>
        );
    }

    const memberSinceDate = userData.memberSince ? new Date(userData.memberSince) : new Date(userData.createdAt);
    const memberSinceYear = memberSinceDate.getFullYear();
    const yearsAsMember = new Date().getFullYear() - memberSinceYear;

    const formatDate = (dateString) => {
        if (!dateString) return 'Não disponível';
        const date = new Date(dateString);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
    };

    const getDisplayEmail = (email) => {
        if (!email) return 'N/A';
        const atIndex = email.lastIndexOf('@');
        if (atIndex === -1) return email;
        const localPart = email.substring(0, atIndex);
        const domain = email.substring(atIndex);
        const plusIndex = localPart.indexOf('+');
        if (plusIndex !== -1) return localPart.substring(0, plusIndex) + domain;
        return email;
    };

    const memberData = {
        name: `${userData.firstName} ${userData.lastName}`,
        memberNumber: userData.memberProfile?.membershipNumber || userData.id.toString().padStart(4, '0'),
        memberSince: memberSinceYear.toString(),
        yearsAsMember: yearsAsMember,
        email: getDisplayEmail(userData.email),
        phone: userData.phone || 'Não definido',
        address: userData.address || 'Não definido',
        postalCode: userData.postalCode && userData.city ? `${userData.postalCode} ${userData.city}` : 'Não definido',
        nif: userData.nif || 'Não definido',
        nextPayment: paymentData?.nextPeriodMonth
            ? (() => { const M = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']; return `${M[paymentData.nextPeriodMonth - 1]} ${paymentData.nextPeriodYear}`; })()
            : 'Mês Atual',
        status: paymentData?.paymentStatus || (userData.isActive ? 'Ativo' : 'Inativo')
    };

    return (
        <div className="dashboard-wrapper">
            {isSwitchingUser && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 15, 38, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 99999,
                    animation: 'fadeInOverlay 0.3s ease-out forwards'
                }}>
                    <style>{`
                        @keyframes fadeInOverlay {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        @keyframes pulseLogo {
                            0% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                            50% { transform: scale(1.05); opacity: 1; filter: drop-shadow(0 0 25px rgba(255,255,255,0.3)); }
                            100% { transform: scale(0.95); opacity: 0.85; filter: drop-shadow(0 0 10px rgba(255,255,255,0.1)); }
                        }
                        @keyframes rotateSpinner {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '24px',
                        textAlign: 'center',
                        padding: '32px',
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
                        maxWidth: '400px',
                        width: '90%'
                    }}>
                        <div style={{ position: 'relative', width: '90px', height: '90px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img
                                src="/CDP_logo.png"
                                alt="CDP Logo"
                                style={{
                                    width: '64px',
                                    height: '64px',
                                    objectFit: 'contain',
                                    animation: 'pulseLogo 2s ease-in-out infinite',
                                    zIndex: 2
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                width: '84px',
                                height: '84px',
                                borderRadius: '50%',
                                border: '3px solid transparent',
                                borderTopColor: '#003380',
                                borderRightColor: '#0044ff',
                                animation: 'rotateSpinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
                                zIndex: 1
                            }}></div>
                        </div>
                        <div>
                            <h3 style={{
                                color: 'white',
                                margin: '0 0 8px 0',
                                fontSize: '1.25rem',
                                fontWeight: '700',
                                letterSpacing: '-0.025em'
                            }}>
                                A alterar perfil...
                            </h3>
                            {switchingToName && (
                                <p style={{
                                    color: '#94a3b8',
                                    margin: 0,
                                    fontSize: '0.95rem',
                                    fontWeight: '500'
                                }}>
                                    A entrar como <span style={{ color: '#60a5fa', fontWeight: '600' }}>{switchingToName}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="dashboard-bg-decoration">
                <div className="blob blob-1"></div>
            </div>

            {paymentToast && (
                <div className={`payment-toast ${paymentToast}`}>
                    <div className="toast-content">
                        <div className={`toast-icon ${paymentToast}`}>
                            <i className={paymentToast === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}></i>
                        </div>
                        <div className="toast-text">
                            <strong>
                                {paymentToast === 'success' ? 'Pagamento Concluído!' : 'Pagamento Interrompido'}
                            </strong>
                            <p>
                                {paymentToast === 'success'
                                    ? 'O processo foi iniciado com sucesso. Poderá demorar alguns minutos a atualizar no sistema.'
                                    : 'O processo de pagamento foi cancelado. Pode tentar novamente quando desejar.'}
                            </p>
                        </div>
                        <button className="toast-close" onClick={() => setPaymentToast(null)}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            )}

            {linkedUsers.length > 1 && (
                <div className="athlete-tabs">
                    <div className="container athlete-tabs-container">
                        {linkedUsers.map((lu) => (
                            <button
                                key={lu.id}
                                onClick={() => handleLinkedTabClick(lu)}
                                className={`athlete-tab ${currentUserId === lu.id ? 'active' : ''} ${lu.requiresConsent ? 'is-blocked' : ''}`}
                                title={lu.requiresConsent ? 'Requer Autorização (18+ anos)' : lu.requiresPin ? 'Requer PIN (18+ anos)' : ''}
                            >
                                {lu.requiresConsent ? (
                                    <FaLock style={{ color: '#ef4444', marginRight: '6px' }} />
                                ) : lu.requiresPin ? (
                                    <FaLock style={{ color: '#eab308', marginRight: '6px' }} />
                                ) : null}
                                {`${lu.firstName} ${lu.lastName}`.trim() || `Conta ${lu.id}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <header className="profile-header">
                <div className="container">
                    <div className="profile-content">
                        <div className="profile-content-left">
                            <div className="socio-avatar-wrapper" onClick={handleEditProfile} title="Alterar foto de perfil">
                                <img
                                    src={userData?.profilePhotoUrl
                                        ? `${API_BASE_URL}${userData.profilePhotoUrl}`
                                        : "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"}
                                    alt={memberData.name}
                                    className="profile-avatar"
                                    onError={(e) => { e.target.src = "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"; }}
                                />
                                <div className="socio-avatar-overlay"><i className="fas fa-camera"></i></div>
                            </div>
                            <div className="profile-info">
                                <h1>{memberData.name}</h1>
                                <div className="profile-meta">
                                    <div className="profile-meta-item">
                                        <FaIdCard />
                                        <span>Sócio #{memberData.memberNumber}</span>
                                    </div>
                                    <div className="profile-meta-item">
                                        <FaCalendarAlt />
                                        <span>Inscrito desde {memberData.memberSince}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {userData?.coachProfile?.isAssistant && (
                            <button
                                className="role-switch-btn"
                                onClick={() => navigate('/dashboard-treinador')}
                                title="Ver Dashboard Treinador Adjunto"
                            >
                                <i className="fas fa-chalkboard-teacher"></i>
                                <span>Ver como Treinador Adjunto</span>
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <section className="quick-stats">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <h3>{memberData.yearsAsMember}</h3>
                            <p>Anos de Sócio</p>
                        </div>
                        <div className="stat-card">
                            <h3><FaGift style={{ color: '#6366f1' }} /></h3>
                            <p>Benefícios Ativos</p>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div className="container">
                    <div className="dashboard-content">
                        <div className="dashboard-main-column">
                            <MembershipCard
                                memberNumber={memberData.memberNumber}
                                name={memberData.name}
                                memberSince={memberData.memberSince}
                                status={memberData.status}
                                cardType="socio"
                                userId={currentUserId}
                                compact={false}
                                photoUrl={userData?.profilePhotoUrl}
                                paymentState={getCardPaymentState(paymentData?.status, overdueMonths)}
                            />

                            <div className="dashboard-card" style={{ marginTop: '32px' }}>
                                <div className="dashboard-card-header">
                                    <h2><FaCreditCard /> Estado de Pagamento</h2>
                                    <button className="view-all-link" onClick={() => setIsHistoryModalOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-blue)', fontWeight: 700 }}>
                                        Ver Histórico <FaArrowRight />
                                    </button>
                                </div>

                                <div className="payment-summary-new">
                                    {isPollingPayment && (
                                        <div className="payment-polling-alert" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '12px 16px',
                                            marginBottom: '16px',
                                            background: 'rgba(0, 51, 128, 0.05)',
                                            border: '1px solid rgba(0, 51, 128, 0.15)',
                                            borderRadius: '12px',
                                            color: '#003380',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            width: '100%'
                                        }}>
                                            <FaSyncAlt style={{ animation: 'spin-subtle 2s linear infinite' }} />
                                            <span>A atualizar estado do pagamento...</span>
                                            <style>{`
                                                @keyframes spin-subtle {
                                                    0% { transform: rotate(0deg); }
                                                    100% { transform: rotate(360deg); }
                                                }
                                            `}</style>
                                        </div>
                                    )}
                                    <div className="payment-summary-main">
                                        <div className="payment-summary-item-new large">
                                            <p>Total em Aberto</p>
                                            <h3 className={totalDue > 0 ? 'text-danger' : 'text-success'} style={{ color: totalDue > 0 ? '#ef4444' : '#10b981' }}>
                                                {totalDue !== null ? `${Number(totalDue).toFixed(2)} €` : '0.00 €'}
                                            </h3>
                                            <span className="total-due-explanation">
                                                {overdueMonths.length > 0
                                                    ? `Quota atual + ${overdueMonths.length} em atraso`
                                                    : 'Regularizada (Mês Atual)'}
                                            </span>
                                        </div>
                                        <div className="payment-summary-details">
                                            <div className="payment-summary-item-new">
                                                <p>{paymentData?.paymentPreference === 'Annual' ? 'Quota Anual' : 'Quota Mensal'}</p>
                                                <span style={{ fontWeight: 700, color: 'var(--primary-blue)' }}>{memberQuota !== null ? `${Number(memberQuota).toFixed(2)} €` : '—'}</span>
                                            </div>
                                            <div className="payment-summary-item-new">
                                                <p>Validade</p>
                                                <span style={{ fontWeight: 700, color: 'var(--primary-blue)' }}>{memberData.nextPayment}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {overdueMonths.length > 0 && (
                                        <div className="overdue-dropdown-container" style={{ marginTop: '20px' }}>
                                            <button
                                                className={`overdue-dropdown-header ${isOverdueDropdownOpen ? 'active' : ''}`}
                                                onClick={() => setIsOverdueDropdownOpen(!isOverdueDropdownOpen)}
                                                style={{ width: '100%', padding: '12px', borderRadius: '12px', background: '#fee2e2', border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                                            >
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', color: '#b91c1c', fontWeight: 700 }}>
                                                    <FaExclamationCircle />
                                                    <span>{overdueMonths.length} {overdueMonths.length === 1 ? 'Quota em Atraso' : 'Quotas em Atraso'}</span>
                                                </div>
                                                {isOverdueDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>

                                            {isOverdueDropdownOpen && (
                                                <div className="overdue-list" style={{ padding: '10px', background: 'white', borderRadius: '0 0 12px 12px', border: '1px solid #f1f5f9', marginTop: '-4px' }}>
                                                    {overdueMonths.map((m, i) => (
                                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < overdueMonths.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: '0.9rem' }}>
                                                            <span>Mês {m.periodMonth}/{m.periodYear}</span>
                                                            <strong style={{ color: '#ef4444' }}>{m.amount.toFixed(2)} €</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <aside className="dashboard-side-column">
                            <div className="dashboard-card">
                                <div className="dashboard-card-header">
                                    <h2><FaBolt /> Ações Rápidas</h2>
                                </div>
                                <div className="action-buttons">
                                    {(totalDue > 0 || memberQuota > 0) && (
                                        <button
                                            onClick={openPaymentModal}
                                            disabled={startingPayment || isPollingPayment}
                                            className="action-btn action-btn-primary"
                                        >
                                            {isPollingPayment ? <FaSyncAlt style={{ animation: 'spin-subtle 2s linear infinite' }} /> : <FaCreditCard />}
                                            {startingPayment ? 'A processar...' : isPollingPayment ? 'A atualizar...' : totalDue > 0 ? `Pagar Quotas (${Number(totalDue).toFixed(2)} €)` : 'Pagar / Antecipar Quota'}
                                        </button>
                                    )}
                                    <button className="action-btn" onClick={() => setIsReceiptsModalOpen(true)}>
                                        <FaReceipt /> Recibos
                                    </button>
                                    <button className="action-btn" onClick={() => setIsTicketsModalOpen(true)} style={{ background: 'linear-gradient(135deg, #003380 0%, #001a40 100%)', color: 'white' }}>
                                        <FaTicketAlt /> Os Meus Bilhetes
                                    </button>
                                    <button className="action-btn" onClick={() => setIsPrivacyModalOpen(true)} style={{ background: 'rgba(0, 51, 128, 0.08)', color: '#003380', fontWeight: 600 }}>
                                        <FaShieldAlt /> Permissões & Privacidade
                                    </button>
                                    <button className="action-btn" onClick={() => setIsFamilyModalOpen(true)}>
                                        <FaUserFriends /> Associar Familiar
                                    </button>
                                    <button className="action-btn" onClick={handleEditProfile}>
                                        <FaEdit /> Editar Perfil
                                    </button>
                                </div>
                            </div>

                            <div className="dashboard-card">
                                <div className="dashboard-card-header">
                                    <h2><FaUser /> Info Pessoal</h2>
                                </div>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <p className="info-label">Email</p>
                                        <p className="info-value">{memberData.email}</p>
                                    </div>
                                    <div className="info-item">
                                        <p className="info-label">Telemóvel</p>
                                        <p className="info-value">{memberData.phone}</p>
                                    </div>
                                    <div className="info-item">
                                        <p className="info-label">Contribuinte</p>
                                        <p className="info-value">{memberData.nif}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="dashboard-card">
                                <div className="dashboard-card-header">
                                    <h2><FaFolder /> Documentos</h2>
                                    <button className="view-all-link" onClick={() => setIsDocumentsModalOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-blue)', fontWeight: 700 }}>
                                        Ver Todos
                                    </button>
                                </div>
                                <div className="documents-list">
                                    <div className="document-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div className="document-icon"><FaFilePdf /></div>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Cartão de Sócio</h4>
                                                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>PDF • 156 KB</span>
                                            </div>
                                        </div>
                                        <button className="document-download" onClick={async () => {
                                            const token = localStorage.getItem('token');
                                            try {
                                                const response = await fetch(`${API_BASE_URL}/api/membershipcard/download?userId=${currentUserId}`, {
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                                if (response.ok) {
                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `Cartao_Socio_${currentUserId}.pdf`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    a.remove();
                                                    window.URL.revokeObjectURL(url);
                                                } else {
                                                    alert('Erro ao gerar cartão. Verifique se é um sócio ativo.');
                                                }
                                            } catch (err) {
                                                console.error('Download failed', err);
                                            }
                                        }}><FaDownload /></button>
                                    </div>
                                    <div className="document-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div className="document-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="fas fa-book"></i></div>
                                            <div>
                                                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Manual do Sócio</h4>
                                                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>PDF • 4.4 MB</span>
                                            </div>
                                        </div>
                                        <a href="/manual-socio.pdf" target="_blank" download="Manual_Socio.pdf" className="document-download" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                                            <FaDownload />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <EditProfileModal
                isOpen={isEditModalOpen}
                onClose={handleCloseModal}
                userData={userData}
                onSave={handleSaveProfile}
            />
            <ReceiptsModal
                isOpen={isReceiptsModalOpen}
                onClose={() => setIsReceiptsModalOpen(false)}
                userId={currentUserId}
            />
            <FamilyAssociationModal isOpen={isFamilyModalOpen} onClose={() => setIsFamilyModalOpen(false)} />
            <DocumentsModal isOpen={isDocumentsModalOpen} onClose={() => setIsDocumentsModalOpen(false)} userId={currentUserId} userType="socio" />
            <PaymentHistorySocio
                isOpen={isHistoryModalOpen}
                onClose={() => setIsHistoryModalOpen(false)}
                userId={currentUserId}
                overdueMonths={overdueMonths}
                paymentStatus={paymentData?.status || paymentData?.paymentStatus || 'Regularizada'}
                quotaAmount={memberQuota || 0}
                paymentPreference={paymentData?.paymentPreference || 'Monthly'}
            />
            <TicketsModal
                isOpen={isTicketsModalOpen}
                onClose={() => setIsTicketsModalOpen(false)}
                userId={currentUserId}
            />

            <AdminPinModal
                isOpen={showAdminPinModal}
                targetUserId={pendingSwitchUser?.id}
                onSuccess={() => {
                    setShowAdminPinModal(false);
                    if (pendingSwitchUser) executeUserSwitch(pendingSwitchUser);
                }}
                onClose={() => setShowAdminPinModal(false)}
            />

            <ConsentRequestModal
                isOpen={showConsentModal}
                targetUser={targetConsentUser}
                onClose={() => setShowConsentModal(false)}
                onRequestSent={() => refreshLinkedUsers()}
            />

            <FamilyPrivacyModal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
            />

            {/* ── Partial / Future Month Selection Modal ───────────────────────── */}
            {showPaymentModal && (
                <div className="pms-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="pms-modal" onClick={e => e.stopPropagation()}>
                        <div className="pms-header">
                            <div className="pms-header-icon"><FaCalendarAlt /></div>
                            <div>
                                <h3 className="pms-title">Selecionar Meses a Pagar</h3>
                                <p className="pms-subtitle">
                                    {allPayableMonths.length} {allPayableMonths.length === 1 ? 'mês disponível' : 'meses disponíveis'} — escolhe os que queres pagar agora
                                </p>
                            </div>
                            <button className="pms-close" onClick={() => setShowPaymentModal(false)}><FaTimes /></button>
                        </div>

                        <div className="pms-select-actions">
                            <button className="pms-select-link"
                                onClick={() => setSelectedMonthKeys(new Set(allPayableMonths.map(m => m.key)))
                                }>Selecionar todos</button>
                            <span className="pms-select-sep">·</span>
                            <button className="pms-select-link"
                                onClick={() => setSelectedMonthKeys(new Set())
                                }>Limpar seleção</button>
                        </div>

                        <div className="pms-months-list">
                            {allPayableMonths.filter(m => !m.isFuture).map((m) => {
                                const checked = selectedMonthKeys.has(m.key);
                                const monthLabel = m.periodMonth
                                    ? new Date(m.periodYear, m.periodMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })
                                    : `Anual ${m.periodYear}`;
                                const label = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
                                return (
                                    <label key={m.key} className={`pms-month-row ${checked ? 'checked' : ''} ${m.isCurrent ? 'is-current' : 'is-overdue'}`}>
                                        <div className="pms-checkbox-wrap">
                                            <input type="checkbox" checked={checked} onChange={() => toggleMonthSel(m.key)} className="pms-checkbox" />
                                            <span className="pms-custom-check">{checked && <FaCheck />}</span>
                                        </div>
                                        <div className="pms-month-info">
                                            <span className="pms-month-name">{label}</span>
                                            {m.isCurrent
                                                ? <span className="pms-badge current">Mês atual</span>
                                                : <span className="pms-badge overdue">Em atraso</span>}
                                        </div>
                                        <span className="pms-month-amount">{m.amount.toFixed(2)} €</span>
                                    </label>
                                );
                            })}

                            {allPayableMonths.some(m => m.isFuture) && (
                                <>
                                    <div className="pms-section-divider"><span>Pagamento antecipado</span></div>
                                    {allPayableMonths.filter(m => m.isFuture).map((m) => {
                                        const checked = selectedMonthKeys.has(m.key);
                                        const monthLabel = new Date(m.periodYear, m.periodMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
                                        const label = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
                                        return (
                                            <label key={m.key} className={`pms-month-row ${checked ? 'checked' : ''} is-future`}>
                                                <div className="pms-checkbox-wrap">
                                                    <input type="checkbox" checked={checked} onChange={() => toggleMonthSel(m.key)} className="pms-checkbox" />
                                                    <span className="pms-custom-check">{checked && <FaCheck />}</span>
                                                </div>
                                                <div className="pms-month-info">
                                                    <span className="pms-month-name">{label}</span>
                                                    <span className="pms-badge future">Antecipado</span>
                                                </div>
                                                <span className="pms-month-amount">{m.amount.toFixed(2)} €</span>
                                            </label>
                                        );
                                    })}
                                </>
                            )}
                        </div>

                        <div className="pms-footer">
                            <div className="pms-total-row">
                                <span className="pms-total-label">
                                    Total selecionado
                                    <span className="pms-total-count">({selectedMonthKeys.size} {selectedMonthKeys.size === 1 ? 'mês' : 'meses'})</span>
                                </span>
                                <span className="pms-total-value">{selectedTotal.toFixed(2)} €</span>
                            </div>

                            {(() => {
                                const nonFutureCount = allPayableMonths.filter(m => !m.isFuture).length;
                                const selNonFuture = allPayableMonths.filter(m => !m.isFuture && selectedMonthKeys.has(m.key)).length;
                                const debt = nonFutureCount - selNonFuture;
                                if (debt <= 0) return null;
                                return (
                                    <p className="pms-remaining-note">
                                        ⚠️ {debt} {debt === 1 ? 'mês ficará' : 'meses ficarão'} em dívida
                                    </p>
                                );
                            })()}

                            <div className="pms-actions">
                                <button className="pms-btn-cancel" onClick={() => setShowPaymentModal(false)}>Cancelar</button>
                                <button
                                    className="pms-btn-confirm"
                                    onClick={handleConfirmModalPayment}
                                    disabled={selectedMonthKeys.size === 0}
                                >
                                    <FaExternalLinkAlt />
                                    Pagar {selectedTotal.toFixed(2)} €
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardSocio;
